import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  type TimeOfDay,
  type DreamscapeImage,
  getTimeOfDay,
  getTimeBlend,
  getSeason,
  getCurrentBackgrounds,
} from '../utils/dreamscape';

interface DreamscapeContextValue {
  timeOfDay: TimeOfDay;
  season: string;
  backgroundUrl: string | null;
  nextBackgroundUrl: string | null;
  blend: number;
}

const DreamscapeContext = createContext<DreamscapeContextValue>({
  timeOfDay: 'morning',
  season: 'winter',
  backgroundUrl: null,
  nextBackgroundUrl: null,
  blend: 0,
});

export const useDreamscape = () => useContext(DreamscapeContext);

const BODY_CLASSES = [
  'dreamscape-morning',
  'dreamscape-afternoon',
  'dreamscape-evening',
  'dreamscape-night',
] as const;

function setBodyClass(timeOfDay: TimeOfDay) {
  const target = `dreamscape-${timeOfDay}`;
  BODY_CLASSES.forEach((cls) => {
    if (cls === target) {
      document.body.classList.add(cls);
    } else {
      document.body.classList.remove(cls);
    }
  });
}

export const DreamscapeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<DreamscapeContextValue>(() => {
    const tod = getTimeOfDay();
    return {
      timeOfDay: tod,
      season: getSeason(),
      backgroundUrl: null,
      nextBackgroundUrl: null,
      blend: 0,
    };
  });

  const update = useCallback(async () => {
    const tod = getTimeOfDay();
    const season = getSeason();
    const { current: currentTime, blend } = getTimeBlend();
    const bgs = await getCurrentBackgrounds();

    setBodyClass(currentTime);

    setState({
      timeOfDay: currentTime,
      season,
      backgroundUrl: bgs.current?.url ?? null,
      nextBackgroundUrl: bgs.next?.url ?? null,
      blend,
    });
  }, []);

  useEffect(() => {
    update();
    const interval = setInterval(update, 60_000);
    return () => {
      clearInterval(interval);
      // Clean up body classes
      BODY_CLASSES.forEach((cls) => document.body.classList.remove(cls));
    };
  }, [update]);

  return <DreamscapeContext.Provider value={state}>{children}</DreamscapeContext.Provider>;
};

export default DreamscapeContext;
